/**
 * /two-factor — second-factor challenge.
 *
 * `flow` search param disambiguates:
 *   - `sign-in`: user already authenticated with password; we need
 *     one of the account's actual second factors. Reads
 *     `useSignIn()`, and branches on `signIn.supportedSecondFactors`
 *     rather than assuming TOTP — an account can be enrolled in
 *     `phone_code` (SMS) only, e.g. via an instance-wide MFA
 *     requirement tied to a verified phone number, with no
 *     authenticator app ever set up. `phone_code` additionally needs
 *     an explicit `prepareSecondFactor` call to trigger the SMS send
 *     (TOTP and backup codes don't).
 *   - `enroll`: post-TOTP-enrollment smoke test (Settings → Security).
 *     Stage 3 keeps this simple — enrollment lives entirely inside
 *     Settings, so `flow=enroll` lands users back at the settings
 *     page after a successful attempt. Today it also uses
 *     `attemptSecondFactor` through `useSignIn()`, but practically
 *     Settings handles enrollment inline and doesn't redirect here.
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OTPField, OTPFieldInput } from '@/components/ui/otp-field';
import {
  activateSession,
  attemptBackupCode,
  attemptPhoneCode,
  attemptTotp,
  prepareSecondFactorPhoneCode,
} from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import type { SignInSecondFactor } from '@clerk/shared/types';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/_auth/two-factor')({
  component: TwoFactorPage,
  validateSearch: (search): { flow?: 'sign-in' | 'enroll'; redirect?: string } => ({
    flow: search.flow === 'enroll' ? 'enroll' : 'sign-in',
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

type Mode = 'totp' | 'phone' | 'backup';

function pickDefaultMode(factors: SignInSecondFactor[]): Mode | null {
  if (factors.some((f) => f.strategy === 'totp')) return 'totp';
  if (factors.some((f) => f.strategy === 'phone_code')) return 'phone';
  if (factors.some((f) => f.strategy === 'backup_code')) return 'backup';
  return null;
}

function TwoFactorPage(): ReactElement {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  const factors = (signIn?.supportedSecondFactors ?? []) as SignInSecondFactor[];
  const phoneFactor = factors.find(
    (f): f is Extract<SignInSecondFactor, { strategy: 'phone_code' }> =>
      f.strategy === 'phone_code',
  );
  const totpAvailable = factors.some((f) => f.strategy === 'totp');
  const backupAvailable = factors.some((f) => f.strategy === 'backup_code');

  // Picked once we know which factors this account actually has,
  // instead of always defaulting to TOTP. `explicitMode` tracks the
  // user's own toggle choice and takes priority once set.
  const [explicitMode, setExplicitMode] = useState<Mode | null>(null);
  const mode = explicitMode ?? pickDefaultMode(factors);

  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);

  const submittingRef = useRef(false);

  // phone_code needs an explicit prepare call to trigger the SMS —
  // unlike TOTP/backup codes, there's no code to enter until we ask
  // Clerk to send one.
  useEffect(() => {
    if (mode !== 'phone' || !signIn || !phoneFactor || phoneCodeSent) return;
    setPhoneCodeSent(true);
    void prepareSecondFactorPhoneCode(signIn, phoneFactor.phoneNumberId).then((result) => {
      if (result.kind === 'error') {
        setFormError(result.formError ?? 'Unable to send a code. Try again.');
      }
    });
  }, [mode, signIn, phoneFactor, phoneCodeSent]);

  async function finish(
    attempt: typeof attemptTotp | typeof attemptBackupCode | typeof attemptPhoneCode,
    value: string,
  ) {
    if (!isLoaded || !signIn || !setActive) return;
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError(undefined);

    const result = await attempt(signIn, value);

    if (result.kind === 'error') {
      setFormError(result.fieldErrors.code ?? result.formError);
      if (mode === 'totp' || mode === 'phone') setCode('');
      if (mode === 'backup') setBackupCode('');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    await activateSession({ setActive, sessionId: result.sessionId, redirectUrl: redirect ?? '/' });
  }

  function onCodeChange(value: string) {
    setCode(value);
    if (value.length === 6) {
      void finish(mode === 'phone' ? attemptPhoneCode : attemptTotp, value);
    }
  }

  function onBackupSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!backupCode) return;
    void finish(attemptBackupCode, backupCode);
  }

  function switchMode(next: Mode) {
    setExplicitMode(next);
    setFormError(undefined);
    setCode('');
    setBackupCode('');
    if (next !== 'phone') setPhoneCodeSent(false);
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-6 text-sm text-[var(--color-mute)]">
        Loading…
      </div>
    );
  }

  if (!signIn || signIn.status !== 'needs_second_factor' || mode === null) {
    return (
      <div className="space-y-3 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">No sign-in in progress</h1>
        <p className="text-sm text-[var(--color-mute)]">Start again from the sign-in page.</p>
        <Button onClick={() => navigate({ to: '/sign-in' })}>Sign in</Button>
      </div>
    );
  }

  const otherModes: { mode: Mode; label: string }[] = [
    totpAvailable && mode !== 'totp'
      ? { mode: 'totp' as const, label: 'Use authenticator app' }
      : null,
    phoneFactor && mode !== 'phone' ? { mode: 'phone' as const, label: 'Use SMS code' } : null,
    backupAvailable && mode !== 'backup'
      ? { mode: 'backup' as const, label: 'Use a backup code' }
      : null,
  ].filter((m): m is { mode: Mode; label: string } => m !== null);

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Two-factor required</h1>
        <p className="text-sm text-[var(--color-mute)]">
          {mode === 'totp' && 'Enter the 6-digit code from your authenticator app.'}
          {mode === 'phone' &&
            (phoneFactor?.safeIdentifier
              ? `Enter the 6-digit code sent to ${phoneFactor.safeIdentifier}.`
              : 'Enter the 6-digit code sent to your phone.')}
          {mode === 'backup' && 'Enter one of the 10 backup codes you saved when enabling 2FA.'}
        </p>
      </header>
      {mode === 'totp' || mode === 'phone' ? (
        <div className="flex flex-col items-center gap-4">
          <OTPField length={6} value={code} onValueChange={onCodeChange} disabled={submitting}>
            {Array.from({ length: 6 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length OTP slots
              <OTPFieldInput key={i} />
            ))}
          </OTPField>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={onBackupSubmit}>
          <Input
            autoFocus
            placeholder="abcd-efgh"
            value={backupCode}
            onChange={(event) => setBackupCode((event.target as HTMLInputElement).value)}
            aria-invalid={formError ? true : undefined}
          />
          <Button type="submit" className="w-full" disabled={submitting || !backupCode}>
            {submitting ? 'Verifying…' : 'Verify backup code'}
          </Button>
        </form>
      )}
      {formError && (
        <div
          className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
          role="alert"
        >
          {formError}
        </div>
      )}
      {otherModes.length > 0 && (
        <div className="flex flex-col items-center gap-2">
          {otherModes.map((m) => (
            <button
              key={m.mode}
              type="button"
              onClick={() => switchMode(m.mode)}
              className="text-sm font-medium text-[var(--color-accent-brand)] hover:underline"
            >
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
